import { useNavigate } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { Heading, Text, Card, Flex, Grid, Box, Spinner } from '@radix-ui/themes'
import { Search } from 'lucide-react'
import { useMe } from '../../hooks/useMe'
import { useBasePath } from '../../hooks/useBasePath'
import { usePermission } from '../../../hooks/usePermission'

export function OnboardingDashboard() {
  const { user } = useAuth()
  const { data: meData, isLoading } = useMe()
  const basePath = useBasePath()
  const navigate = useNavigate()
  const { can } = usePermission()

  if (isLoading) {
    return (
      <Flex align="center" justify="center" style={{ height: 256 }}>
        <Flex direction="column" align="center" gap="3">
          <Spinner size="3" />
          <Text size="2" color="gray">Loading dashboard...</Text>
        </Flex>
      </Flex>
    )
  }

  return (
    <Box>
      <Heading size={{ initial: '5', sm: '6' }} mb="2">Dashboard</Heading>
      <Text size={{ initial: '2', sm: '3' }} color="gray" mb={{ initial: '4', sm: '6' }} as="p">
        Welcome back, {user?.firstName || meData?.first_name || 'there'}!
      </Text>

      {/* Stat Cards */}
      <Grid columns={{ initial: '1', md: '3' }} gap={{ initial: '3', sm: '5' }} mb={{ initial: '4', sm: '6' }}>
        <Card size={{ initial: '2', sm: '3' }}>
          <Text size="2" weight="medium" color="gray">Tenant</Text>
          <Flex align="center" gap={{ initial: '2', sm: '3' }} mt="2">
            {meData?.tenant?.logo_url ? (
              <img
                src={meData.tenant.logo_url}
                alt={meData.tenant.name}
                className="h-8 w-8 sm:h-10 sm:w-10 object-contain rounded flex-shrink-0"
              />
            ) : null}
            <Text size={{ initial: '4', sm: '5' }} weight="bold" className="truncate">
              {meData?.tenant?.name || 'N/A'}
            </Text>
          </Flex>
          <Text size="1" color="gray" mt="1" as="p">Your current tenant</Text>
        </Card>

        <Card size={{ initial: '2', sm: '3' }}>
          <Text size="2" weight="medium" color="gray">Your role</Text>
          <Text size={{ initial: '4', sm: '5' }} weight="bold" mt="2" as="p" className="capitalize">
            {meData?.role === 'member' ? 'Engineer' : meData?.role?.replace('_', ' ') || 'N/A'}
          </Text>
          <Text size="1" color="gray" mt="1" as="p">Access level in this tenant</Text>
        </Card>

        <Card size={{ initial: '2', sm: '3' }}>
          <Text size="2" weight="medium" color="gray">Brand colour</Text>
          <Flex align="center" gap={{ initial: '2', sm: '3' }} mt="2">
            <Box
              className="rounded-lg border border-gray-300 flex-shrink-0"
              style={{
                backgroundColor: meData?.tenant?.primary_color || '#1f2937',
                width: 32,
                height: 32,
              }}
            />
            <Text size="2" className="font-mono" color="gray">
              {meData?.tenant?.primary_color || '#1f2937'}
            </Text>
          </Flex>
        </Card>
      </Grid>

      {/* Engineer Portal Switcher */}
      {can('search_products') && (
        <Card
          className="hover:border-brand-primary hover:shadow-md transition-all cursor-pointer border-2 border-dashed border-gray-300"
          onClick={() => navigate(`${basePath}/engineer`)}
        >
          <Flex align="center" gap={{ initial: '3', sm: '4' }} p={{ initial: '1', sm: '2' }}>
            <Flex
              align="center"
              justify="center"
              className="bg-brand-primary-lightest rounded-lg flex-shrink-0"
              style={{ width: 40, height: 40 }}
            >
              <Search size={20} className="text-brand-primary" />
            </Flex>
            <Box className="min-w-0">
              <Text size={{ initial: '2', sm: '3' }} weight="bold" as="p">Engineer portal</Text>
              <Text size={{ initial: '1', sm: '2' }} color="gray" as="p">Access product search and ordering</Text>
            </Box>
          </Flex>
        </Card>
      )}
    </Box>
  )
}
